import React from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Button, Box, Typography, Avatar, Chip } from '@mui/material';
import { AccountBalanceWallet, ExpandMore } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

/**
 * Custom Rainbow Kit Connect Button with MUI styling
 * Matches the design aesthetic of your DEX management components
 */
export const RainbowKitCustomConnectButton: React.FC = () => {
    const theme = useTheme();

    return (
        <ConnectButton.Custom>
            {({ account, chain, openAccountModal, openChainModal, openConnectModal, mounted }) => {
                const ready = mounted;
                const connected = ready && account && chain;

                return (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {(() => {
                            if (!connected) {
                                return (
                                    <Button
                                        onClick={openConnectModal}
                                        variant="contained"
                                        startIcon={<AccountBalanceWallet />}
                                        sx={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            fontWeight: 600,
                                            px: 3,
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                                                transform: 'translateY(-2px)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        Connect Wallet
                                    </Button>
                                );
                            }

                            if (chain.unsupported) {
                                return (
                                    <Button
                                        onClick={openChainModal}
                                        variant="contained"
                                        color="error"
                                        sx={{
                                            fontWeight: 600,
                                            px: 3,
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                        }}
                                    >
                                        Wrong Network
                                    </Button>
                                );
                            }

                            return (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {/* Chain Selector */}
                                    <Button
                                        onClick={openChainModal}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            minWidth: 'auto',
                                            px: 2,
                                            py: 1,
                                            borderRadius: 2,
                                            borderColor: theme.palette.primary.main,
                                            color: theme.palette.primary.main,
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {chain.hasIcon && (
                                                <Box
                                                    sx={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: '50%',
                                                        overflow: 'hidden',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {chain.iconUrl && (
                                                        <img
                                                            alt={chain.name ?? 'Chain icon'}
                                                            src={chain.iconUrl}
                                                            style={{ width: 20, height: 20 }}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {chain.name}
                                            </Typography>
                                        </Box>
                                    </Button>

                                    {/* Account Button */}
                                    <Button
                                        onClick={openAccountModal}
                                        variant="contained"
                                        sx={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            fontWeight: 600,
                                            px: 2,
                                            py: 1,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.5)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {/* Account Avatar */}
                                            <Avatar
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                                    fontSize: '0.75rem',
                                                }}
                                            >
                                                {account.ensName
                                                    ? account.ensName[0].toUpperCase()
                                                    : account.address[2].toUpperCase()}
                                            </Avatar>

                                            {/* Account Info */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Typography variant="body2" sx={{
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                    color: '#FFFFFF',
                                                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                                }}>
                                                    {account.ensName || `${account.address.slice(0, 6)}...${account.address.slice(-4)}`}
                                                </Typography>
                                                {account.displayBalance && (
                                                    <Typography variant="caption" sx={{
                                                        lineHeight: 1,
                                                        color: '#FFFFFF',
                                                        opacity: 0.95,
                                                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                                    }}>
                                                        {account.displayBalance}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <ExpandMore sx={{ fontSize: 16, opacity: 0.7 }} />
                                        </Box>
                                    </Button>
                                </Box>
                            );
                        })()}
                    </Box>
                );
            }}
        </ConnectButton.Custom>
    );
};

export default RainbowKitCustomConnectButton;
